<script lang="ts">
import { useNewsTypes, useTerms } from '~/api/drupal'
export const useNewsCategories = async () => {
  const route = useRoute()

  const queryParamsType = Array.isArray(route.query.type)
    ? route.query.type?.filter(Boolean)
    : route.query.type
      ? [route.query.type as string]
      : []

  const { options: newsTypes } = await useNewsTypes()

  const activeNewsTypes = ref(
    queryParamsType.length
      ? newsTypes.filter((option) => queryParamsType.includes(option.value)).map((option) => option.value)
      : newsTypes.map((option) => option.value),
  )

  return { newsTypes, activeNewsTypes }
}

export const useNewsTags = async () => {
  const route = useRoute()

  const queryParamsTag = Array.isArray(route.query.tag)
    ? route.query.tag?.filter(Boolean)
    : route.query.tag
      ? [route.query.tag as string]
      : []

  const { terms } = await useTerms(['visible_tags'])

  const newsTags = terms.value.visible_tags

  const activeNewsTags = ref(
    queryParamsTag.length
      ? newsTags.filter((option) => queryParamsTag.includes(option.id)).map((option) => option.id)
      : newsTags.map((option) => option.id),
  )

  return { newsTags, activeNewsTags }
}
</script>
<script setup lang="ts">
import { useNews } from '~/api/drupal'

import { useScrollToTargetOnChange } from '~/composables/useScrollToTargetOnChange'

useHead({
  bodyAttrs: {
    class: 'news-listing',
  },
})

const { t } = useI18n()

const { newsTypes, activeNewsTypes } = await useNewsCategories()
const { newsTags, activeNewsTags } = await useNewsTags()

const { items, page, maxPages, noResults } = await useNews(activeNewsTypes, newsTypes, activeNewsTags)
const { scrollTarget } = useScrollToTargetOnChange(page)
</script>

<template>
  <SectionFilters class="types">
    <div v-animate="{ animation: 'vertical-reveal' }">
      <Checkbox
        id="news-listing-types"
        v-model="activeNewsTypes"
        class="news-type"
        :options="newsTypes"
        :required="false"
      />
    </div>
  </SectionFilters>
  <SectionFilters>
    <Tags v-model:active="activeNewsTags" :tags="newsTags" :all="true" />
  </SectionFilters>
  <Section>
    <ul ref="scrollTarget" class="items cul tl-11 tl-o-1">
      <li
        v-for="(item, index) in items"
        :key="`${index}-${item?.id}`"
        v-animate="{ animation: 'vertical-reveal' }"
        class="item"
      >
        <component :is="item.variant === 'style_2' ? 'CardText' : 'CardImage'" v-bind="item" title-tag="h2" />
      </li>
      <li v-if="noResults" v-animate="{ animation: 'vertical-reveal' }">
        {{ t('news.list.noResults') }}
      </li>
    </ul>
    <Pagination v-if="maxPages > 1" v-model:page="page" class="tl-11 tl-o-1" :max="maxPages" />
  </Section>
</template>
<style scoped lang="scss">
.section-filters {
  @include breakpoint(tl down) {
    :deep(.content) {
      position: relative;
      width: calc(50% + 50vw);
      overflow-x: auto;
      overflow-y: hidden;
    }

    :deep(.outer-checkbox) {
      padding-top: 2px;
    }
  }

  &.types {
    margin-bottom: 20px;
    :deep(.news-type .formkit-options) {
      flex-direction: row;
      gap: 25px;
    }
  }
}
.tags {
  @include breakpoint(tl down) {
    flex-wrap: nowrap;
    width: 1000px;
  }

  :deep(li) {
    flex-shrink: 0;
  }
}
.items {
  display: grid;
  gap: 48px;
  @include breakpoint(ts) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 96px 48px;
  }

  @include breakpoint(ds) {
    gap: 128px 86px;
  }
}

.item .card-image :deep(.sliding-content) {
  @include breakpoint(t) {
    grid-column: 1 / -2;
  }
  @include breakpoint(tl) {
    grid-column: 1 / 3;
  }
}

.item:nth-child(4n + 2),
.item:nth-child(4n + 3) {
  .card-image {
    @include breakpoint(t) {
      grid-template-columns: 32px auto 54px 36px;
      :deep(.image) {
        grid-column: 2 / -1;
        grid-row-start: 2;
      }
    }
    @include breakpoint(tl) {
      :deep(.image) {
        grid-column: 2 / -2;
        grid-row-start: 2;
      }
    }
    @include breakpoint(tl) {
      grid-template-columns: 54px auto 54px 48px;
    }
  }
}

.pagination {
  margin-top: 48px;
}
</style>
